import React, { useState, useEffect } from 'react';
import {
  Clock,
  Flag,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RotateCcw,
  Home,
  Check,
  Zap,
  Award,
  BookOpen,
  Send,
  LogIn,
  Image as ImageIcon,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { Alert } from '../components/ui/Alert';
import { Progress } from '../components/ui/Progress';
import { Question, CompleteExamResponse, ExtendedTrackType, ExamMode, User } from '../../shared/types';
import { api, authStorage } from '../lib/api';
import { formatTime } from '../lib/utils';

export interface ExamPageProps {
  track: ExtendedTrackType;
  mode: ExamMode;
  categoryId?: string;
  questionCount?: number;
  user?: User | null;
  onFinishExam: () => void;
  onNavigateHome: () => void;
  onOpenAuthModal: () => void;
}

export function ExamPage({
  track,
  mode,
  categoryId,
  questionCount,
  user,
  onFinishExam,
  onNavigateHome,
  onOpenAuthModal,
}: ExamPageProps) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<Record<string, number>>({});
  const [answeredFeedback, setAnsweredFeedback] = useState<Record<string, { isCorrect: boolean; explanation?: string; correctIndex?: number }>>({});
  const [flaggedQuestions, setFlaggedQuestions] = useState<Record<string, boolean>>({});
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [initialTimeLimit, setInitialTimeLimit] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [examResult, setExamResult] = useState<CompleteExamResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Initialize Exam Session
  useEffect(() => {
    async function initSession() {
      try {
        setIsLoading(true);
        setErrorMessage(null);

        // Check if user is logged in via prop, authStorage, or cookie session
        const currentUser = user || authStorage.getUser();
        if (!currentUser && !authStorage.getToken()) {
          setErrorMessage('Please sign in or create an account to start an assessment and record your performance history.');
          setIsLoading(false);
          return;
        }

        const targetCount = questionCount || (mode === 'timed_mock' ? (categoryId ? 15 : 30) : 15);
        const targetTime = mode === 'timed_mock' ? Math.min(targetCount * 60, 3600) : 0;

        const session = await api.exams.start({
          track,
          mode,
          categoryId,
          questionCount: targetCount,
          timeLimitSeconds: targetTime,
        });

        setSessionId(session.sessionId);
        setQuestions(session.questions);
        const effectiveLimit = session.timeLimitSeconds > 0
          ? session.timeLimitSeconds
          : (mode === 'timed_mock' ? Math.min(session.questions.length * 60, 3600) : 0);
        setTimeRemaining(effectiveLimit);
        setInitialTimeLimit(effectiveLimit);
      } catch (err: any) {
        console.error('Exam initialization failed', err);
        setErrorMessage(err.message || 'Failed to start exam session');
      } finally {
        setIsLoading(false);
      }
    }

    initSession();
  }, [track, mode, categoryId, questionCount, user]);

  // Timer Countdown for Timed Mock
  useEffect(() => {
    if (mode !== 'timed_mock' || examResult || isLoading) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmitExam();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [mode, examResult, isLoading]);

  const handleSelectOption = async (optionIndex: number) => {
    if (!sessionId || !currentQuestion || isSubmitting || examResult) return;

    const qId = currentQuestion.id;
    setUserAnswers((prev) => ({ ...prev, [qId]: optionIndex }));

    try {
      const res = await api.exams.submitAnswer(sessionId, {
        questionId: qId,
        selectedIndex: optionIndex,
        timeSpentMs: 15000,
      });

      if (mode === 'study') {
        setAnsweredFeedback((prev) => ({
          ...prev,
          [qId]: {
            isCorrect: res.isCorrect,
            explanation: res.explanation || currentQuestion.explanation,
            correctIndex: res.correctIndex !== undefined ? res.correctIndex : currentQuestion.correctIndex,
          },
        }));
      }
    } catch (err) {
      console.error('Failed to submit answer', err);
    }
  };

  const handleToggleFlag = (qId: string) => {
    setFlaggedQuestions((prev) => ({
      ...prev,
      [qId]: !prev[qId],
    }));
  };

  const handleSubmitExam = async () => {
    if (!sessionId || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const result = await api.exams.complete(sessionId);
      setExamResult(result);
    } catch (err: any) {
      console.error('Failed to finalize exam', err);
      setErrorMessage(err.message || 'Failed to complete exam');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container max-w-4xl py-20 text-center">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-amber-500 border-t-transparent" />
        <h2 className="text-xl font-bold text-foreground dark:text-white mt-4">Generating Practice Exam Session...</h2>
        <p className="text-xs text-muted-foreground mt-2">Selecting practice questions and preparing schematics at the edge.</p>
      </div>
    );
  }

  if (errorMessage) {
    const isAuthError =
      errorMessage.toLowerCase().includes('sign in') ||
      errorMessage.toLowerCase().includes('account') ||
      errorMessage.toLowerCase().includes('unauthorized') ||
      errorMessage.toLowerCase().includes('authentication') ||
      errorMessage.toLowerCase().includes('not logged in');

    return (
      <div className="container max-w-2xl py-16 space-y-4">
        <Alert variant="destructive" title="Unable to start exam">
          {errorMessage}
        </Alert>
        <div className="flex flex-wrap gap-3 justify-center">
          {isAuthError && (
            <Button variant="amber" onClick={onOpenAuthModal}>
              <LogIn className="h-4 w-4 mr-1.5" /> Sign In / Create Account
            </Button>
          )}
          <Button variant="secondary" onClick={onNavigateHome}>
            <Home className="h-4 w-4 mr-1.5" /> Return Home
          </Button>
          <Button variant={isAuthError ? 'outline' : 'amber'} onClick={() => window.location.reload()}>
            <RotateCcw className="h-4 w-4 mr-1.5" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;
  const answeredCount = Object.keys(userAnswers).length;

  // --------------------------------------------------------------------------
  // Results Screen
  // --------------------------------------------------------------------------
  if (examResult) {
    return (
      <div className="container max-w-4xl py-10 space-y-8">
        {/* Score Header Card */}
        <div className="rounded-2xl border border-border dark:border-slate-800 bg-card/90 dark:bg-slate-900/90 p-8 text-center backdrop-blur-md shadow-sm">
          <div className="inline-flex items-center justify-center h-20 w-20 rounded-2xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 mb-4">
            {examResult.passed ? (
              <Award className="h-10 w-10 text-emerald-500 dark:text-emerald-400" />
            ) : (
              <AlertTriangle className="h-10 w-10 text-amber-500 dark:text-amber-400" />
            )}
          </div>

          <Badge variant={examResult.passed ? 'success' : 'warning'} className="text-sm px-3 py-1 mb-3">
            {examResult.passed ? 'PASSED (BENCHMARK MET)' : 'NEEDS PRACTICE (<70%)'}
          </Badge>

          <h1 className="text-3xl font-black text-foreground dark:text-white tracking-tight">
            {examResult.percentage}% Final Score
          </h1>

          <p className="text-sm text-slate-600 dark:text-slate-300 mt-2 max-w-md mx-auto">
            You scored <strong className="text-foreground dark:text-white">{examResult.score}</strong> out of <strong className="text-foreground dark:text-white">{examResult.totalQuestions}</strong> questions correctly.
            {examResult.passed
              ? ' Outstanding performance! You are demonstrating strong readiness for this section on the Ramsay assessment.'
              : ' Review the detailed explanations below to master the questions you missed.'}
          </p>

          <div className="flex justify-center gap-3 mt-6">
            <Button variant="secondary" size="md" onClick={onNavigateHome}>
              <Home className="h-4 w-4 mr-1.5" /> Back to Dashboard
            </Button>
            <Button
              variant="amber"
              size="md"
              onClick={() => window.location.reload()}
            >
              <RotateCcw className="h-4 w-4 mr-1.5" /> Take Another Test
            </Button>
          </div>
        </div>

        {/* Detailed Question Review */}
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-foreground dark:text-white">Question Review & Explanations</h3>

          {examResult.answers.map((ans, idx) => {
            const originalQ = questions.find((q) => q.id === ans.questionId);
            if (!originalQ) return null;

            return (
              <Card
                key={ans.questionId}
                className={`border ${
                  ans.isCorrect
                    ? 'border-emerald-500/30 bg-emerald-500/5 dark:border-emerald-800/40 dark:bg-emerald-950/10'
                    : 'border-rose-500/30 bg-rose-500/5 dark:border-rose-800/40 dark:bg-rose-950/10'
                }`}
              >
                <CardHeader className="p-5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono font-bold text-muted-foreground">Question {idx + 1}</span>
                    <Badge variant={ans.isCorrect ? 'success' : 'danger'} className="text-[10px]">
                      {ans.isCorrect ? 'Correct' : 'Incorrect'}
                    </Badge>
                  </div>
                  <CardTitle className="text-sm font-semibold text-foreground dark:text-white mt-2">
                    {originalQ.prompt}
                  </CardTitle>

                  {originalQ.imageUrl && (
                    <div className="my-3 overflow-hidden rounded-xl border border-border dark:border-slate-800 bg-slate-950 p-2 sm:p-3 text-center shadow-inner">
                      <div className="flex items-center justify-between px-2 pb-1.5 text-xs text-muted-foreground border-b border-border/40 dark:border-slate-800/80 mb-2">
                        <span className="flex items-center gap-1.5 font-semibold text-amber-500 text-[11px]">
                          <ImageIcon className="h-3.5 w-3.5" /> Technical Schematic
                        </span>
                        <span className="font-mono text-[10px] bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded">
                          Figure Ref
                        </span>
                      </div>
                      <img
                        src={originalQ.imageUrl}
                        alt="Question schematic"
                        className="mx-auto max-h-64 w-auto object-contain rounded"
                        loading="lazy"
                      />
                    </div>
                  )}
                </CardHeader>

                <CardContent className="p-5 pt-0 space-y-3">
                  <div className="grid gap-2 text-xs">
                    {originalQ.options.map((opt, optIdx) => {
                      const isSelected = ans.selectedIndex === optIdx;
                      const isCorrect = ans.correctIndex === optIdx;

                      let style = 'border-border dark:border-slate-800 bg-card dark:bg-slate-900/60 text-foreground dark:text-slate-300';
                      if (isCorrect) {
                        style = 'border-emerald-500/80 bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 font-semibold';
                      } else if (isSelected && !isCorrect) {
                        style = 'border-rose-500/80 bg-rose-500/15 text-rose-800 dark:text-rose-300 line-through';
                      }

                      return (
                        <div key={optIdx} className={`flex items-center justify-between p-3 rounded-lg border ${style}`}>
                          <span>{String.fromCharCode(65 + optIdx)}. {opt}</span>
                          {isCorrect && <Check className="h-4 w-4 text-emerald-500 dark:text-emerald-400 shrink-0" />}
                          {isSelected && !isCorrect && <XCircle className="h-4 w-4 text-rose-500 dark:text-rose-400 shrink-0" />}
                        </div>
                      );
                    })}
                  </div>

                  <Alert variant="info" title="Engineering Explanation & Logic">
                    {ans.explanation}
                  </Alert>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // --------------------------------------------------------------------------
  // Active Test Runner Screen
  // --------------------------------------------------------------------------
  const currentSelected = userAnswers[currentQuestion?.id];
  const currentFeedback = answeredFeedback[currentQuestion?.id];
  const isFlagged = !!flaggedQuestions[currentQuestion?.id];

  return (
    <div className="container max-w-5xl py-6 space-y-6 pb-20">
      {/* Top Test Control Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 rounded-xl border border-border dark:border-slate-800 bg-card/90 dark:bg-slate-900/80 px-6 py-4 backdrop-blur-md shadow-sm">
        <div className="flex items-center gap-3">
          <Badge variant={track === 'all' ? 'warning' : (track as any)}>
            {track} track
          </Badge>
          <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
            {mode === 'study'
              ? 'Untimed Study Mode'
              : `${Math.round(initialTimeLimit / 60) || totalQuestions}-Min Timed Simulator`}
          </span>
        </div>

        <div className="flex items-center gap-6">
          {mode === 'timed_mock' && (
            <div className={`flex items-center gap-2 font-mono text-sm font-bold px-3 py-1 rounded-md border ${
              timeRemaining <= 60 || (timeRemaining <= 300 && totalQuestions >= 20)
                ? 'bg-rose-500/20 text-rose-500 dark:text-rose-400 border-rose-500/40 animate-pulse'
                : 'bg-slate-100 dark:bg-slate-800 text-amber-600 dark:text-amber-400 border-slate-200 dark:border-slate-700'
            }`}>
              <Clock className="h-4 w-4" />
              {formatTime(timeRemaining)}
            </div>
          )}

          <div className="text-xs font-mono text-muted-foreground">
            Progress: <strong className="text-foreground dark:text-white">{answeredCount}/{totalQuestions}</strong>
          </div>

          <Button
            variant="amber"
            size="sm"
            onClick={handleSubmitExam}
            isLoading={isSubmitting}
            className="text-xs gap-1.5 font-bold shadow-md shadow-amber-500/20"
          >
            <Send className="h-3.5 w-3.5" />
            Finish & Grade
          </Button>
        </div>
      </div>

      <Progress value={(answeredCount / totalQuestions) * 100} indicatorClassName="bg-amber-400" />

      {/* Main Question & Workspace Layout */}
      <div className="grid gap-6 md:grid-cols-4">
        {/* Left: Question Card (3 cols) */}
        <div className="md:col-span-3 space-y-6">
          <Card className="border-border dark:border-slate-800 bg-card dark:bg-slate-900/90 shadow-sm dark:shadow-xl">
            <CardHeader className="p-6 pb-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono font-bold text-amber-600 dark:text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
                    Question {currentIndex + 1} of {totalQuestions}
                  </span>
                  {currentQuestion?.categoryName && (
                    <span className="text-xs text-muted-foreground font-medium">
                      {currentQuestion.categoryName}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">
                    {currentQuestion?.difficulty || 'Medium'}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => currentQuestion && handleToggleFlag(currentQuestion.id)}
                    className={`h-7 px-2 text-xs gap-1 ${
                      isFlagged ? 'text-amber-600 dark:text-amber-400 bg-amber-500/10' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Flag className={`h-3.5 w-3.5 ${isFlagged ? 'fill-current' : ''}`} />
                    {isFlagged ? 'Flagged' : 'Flag'}
                  </Button>
                </div>
              </div>

              <CardTitle className="text-base sm:text-lg font-medium text-foreground dark:text-white mt-4 leading-relaxed">
                {currentQuestion?.prompt}
              </CardTitle>
            </CardHeader>

            <CardContent className="p-6 pt-2 space-y-4">
              {/* Technical Schematic or Diagram Figure */}
              {currentQuestion?.imageUrl && (
                <div className="overflow-hidden rounded-xl border border-border dark:border-slate-800 bg-slate-950 p-2 sm:p-4 text-center shadow-md">
                  <div className="flex items-center justify-between px-2 pb-2 text-xs text-muted-foreground border-b border-border/50 dark:border-slate-800 mb-3">
                    <span className="flex items-center gap-1.5 font-semibold text-amber-500">
                      <ImageIcon className="h-4 w-4" /> Technical Schematic / Reference Diagram
                    </span>
                    <span className="font-mono text-[10px] bg-slate-800 text-slate-300 px-2 py-0.5 rounded border border-slate-700">
                      Figure Reference
                    </span>
                  </div>
                  <img
                    src={currentQuestion.imageUrl}
                    alt="Technical schematic or reference diagram"
                    className="mx-auto max-h-80 w-auto object-contain rounded-lg"
                    loading="eager"
                  />
                </div>
              )}

              {/* Options List */}
              <div className="space-y-2.5">
                {currentQuestion?.options.map((option, optIdx) => {
                  const isSelected = currentSelected === optIdx;
                  const showStudyAnswer = mode === 'study' && currentFeedback;
                  const isCorrect = currentFeedback?.correctIndex === optIdx;
                  const isWrongSelected = isSelected && currentFeedback && !currentFeedback.isCorrect;

                  let borderStyle = 'border-border dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-700 bg-card dark:bg-slate-950/60 text-foreground dark:text-slate-200';
                  if (isSelected && mode === 'timed_mock') {
                    borderStyle = 'border-amber-500 bg-amber-500/10 text-foreground dark:text-white font-medium ring-1 ring-amber-500/40';
                  } else if (showStudyAnswer) {
                    if (isCorrect) {
                      borderStyle = 'border-emerald-500 bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 font-semibold ring-1 ring-emerald-500';
                    } else if (isWrongSelected) {
                      borderStyle = 'border-rose-500 bg-rose-500/15 text-rose-800 dark:text-rose-300 ring-1 ring-rose-500';
                    }
                  }

                  return (
                    <button
                      key={optIdx}
                      onClick={() => handleSelectOption(optIdx)}
                      className={`w-full text-left p-4 rounded-xl border transition-all flex items-center justify-between text-sm ${borderStyle}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-slate-100 dark:bg-slate-800 text-xs font-mono font-bold text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                          {String.fromCharCode(65 + optIdx)}
                        </span>
                        <span>{option}</span>
                      </div>
                      {showStudyAnswer && isCorrect && (
                        <CheckCircle2 className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" />
                      )}
                      {showStudyAnswer && isWrongSelected && (
                        <XCircle className="h-5 w-5 text-rose-500 dark:text-rose-400 shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Study Mode Instant Feedback Box */}
              {mode === 'study' && currentFeedback && (
                <div className="mt-4 pt-4 border-t border-border dark:border-slate-800">
                  <Alert
                    variant={currentFeedback.isCorrect ? 'success' : 'warning'}
                    title={currentFeedback.isCorrect ? 'Correct Logic!' : 'Review Calculation / Concept'}
                  >
                    {currentFeedback.explanation}
                  </Alert>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Navigation Controls */}
          <div className="flex items-center justify-between">
            <Button
              variant="secondary"
              size="sm"
              disabled={currentIndex === 0}
              onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
              className="gap-1 text-xs"
            >
              <ChevronLeft className="h-4 w-4" /> Previous
            </Button>

            <span className="text-xs text-muted-foreground font-mono">
              {currentIndex + 1} / {totalQuestions}
            </span>

            <Button
              variant="secondary"
              size="sm"
              disabled={currentIndex === totalQuestions - 1}
              onClick={() => setCurrentIndex((prev) => Math.min(totalQuestions - 1, prev + 1))}
              className="gap-1 text-xs"
            >
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Right: Question Navigation Palette (1 col) */}
        <div className="space-y-4">
          <Card className="border-border dark:border-slate-800 bg-card/90 dark:bg-slate-900/60 p-4 shadow-sm">
            <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 font-mono">
              Question Navigator
            </h4>

            <div className="grid grid-cols-5 gap-1.5">
              {questions.map((q, idx) => {
                const isAnswered = userAnswers[q.id] !== undefined;
                const isCurrent = idx === currentIndex;
                const flagged = flaggedQuestions[q.id];

                let btnClass = 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700';
                if (isCurrent) {
                  btnClass = 'bg-amber-500 text-slate-950 font-bold border-amber-400 ring-2 ring-amber-400/40';
                } else if (isAnswered) {
                  btnClass = 'bg-emerald-600/15 dark:bg-emerald-600/30 text-emerald-800 dark:text-emerald-300 border-emerald-500/40';
                }

                return (
                  <button
                    key={q.id}
                    onClick={() => setCurrentIndex(idx)}
                    className={`relative flex h-8 w-full items-center justify-center rounded text-xs font-mono transition-all border ${btnClass}`}
                  >
                    {idx + 1}
                    {flagged && (
                      <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-amber-500" />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 pt-4 border-t border-border dark:border-slate-800 space-y-1.5 text-[11px] text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded bg-amber-500" />
                <span>Current Question</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded bg-emerald-600/20 dark:bg-emerald-600/40 border border-emerald-500/40" />
                <span>Answered</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-amber-500" />
                <span>Flagged for Review</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
